import { useState, useEffect } from "react";
import { FiEdit2 } from "react-icons/fi";
import SkeletonBase from "newui/components/Skeleton";
import { useOverallScores } from "../../hooks/useOverallScores";
import { useAwards } from "../../hooks/useAwards";
import { useMeetingScores } from "../../hooks/useMeetingScores";
import { SupplierAwardCard } from "./SupplierAwardCard";
import { SoloScoreEditModal } from "./SoloScoreEditModal";
import type { AwardStatus } from "./SupplierAwardCard";
import type { OverallSupplierScore, OverallCriteriaScore } from "../../types/meeting.type";

interface AwardState {
  award_status: AwardStatus;
  comment: string;
}

interface EditTarget {
  criteriaId: number;
  criteriaName: string;
  currentScore: number | null;
  inviteId: number;
  supplierName: string;
}

interface SoloAwardViewProps {
  evalId: number;
  rfxId: number;
}

const BELOW_LEADER_THRESHOLD = 0.2;

function getLeaderInviteId(suppliers: OverallSupplierScore[]): number | null {
  let leaderId: number | null = null;
  let leaderScore: number | null = null;

  for (const supplier of suppliers) {
    const score =
      supplier.consensus_weighted_total ?? supplier.pre_meeting_weighted_average;
    if (score !== null && (leaderScore === null || score > leaderScore)) {
      leaderScore = score;
      leaderId = supplier.invite_id;
    }
  }

  return leaderId;
}

function getLeaderScore(
  suppliers: OverallSupplierScore[],
  leaderId: number | null
): number | null {
  if (leaderId === null) {
    return null;
  }
  const leader = suppliers.find((s) => s.invite_id === leaderId);
  if (!leader) {
    return null;
  }
  return leader.consensus_weighted_total ?? leader.pre_meeting_weighted_average;
}

function isBelowThreshold(
  supplier: OverallSupplierScore,
  leaderScore: number | null
): boolean {
  if (leaderScore === null || leaderScore === 0) {
    return false;
  }
  const score =
    supplier.consensus_weighted_total ?? supplier.pre_meeting_weighted_average;
  if (score === null) {
    return false;
  }
  return (leaderScore - score) / leaderScore > BELOW_LEADER_THRESHOLD;
}

const ScoreCell: React.FC<{
  score: number | null;
  onEdit: () => void;
}> = ({ score, onEdit }) => (
  <div className="flex items-center justify-end gap-2">
    <span className="tabular-nums text-sm font-medium text-gray-900">
      {score !== null ? score.toFixed(1) : "—"}
    </span>
    <button
      type="button"
      onClick={onEdit}
      className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
      aria-label="Edit score"
      data-testid="edit-score-button"
    >
      <FiEdit2 size={13} />
    </button>
  </div>
);

// Isolated hook usage per criteria row — used inside the edit modal submit handler
interface ScoreEditorProps {
  editTarget: EditTarget | null;
  evalId: number;
  rfxId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const ScoreEditor: React.FC<ScoreEditorProps> = ({
  rfxId,
  evalId,
  editTarget,
  onClose,
  onSuccess,
}) => {
  const { upsertScores } = useMeetingScores(
    rfxId,
    evalId,
    editTarget?.criteriaId
  );

  const handleSave = (
    _criteriaId: number,
    inviteId: number,
    newScore: number,
    editReason: string
  ) => {
    upsertScores.mutate(
      {
        scores: [
          {
            rfx_invite_id: inviteId,
            consensus_score: newScore,
            edit_reason: editReason,
          },
        ],
      },
      {
        onSuccess: () => {
          onSuccess();
          onClose();
        },
      }
    );
  };

  if (!editTarget) {
    return null;
  }

  return (
    <SoloScoreEditModal
      isOpen
      supplierName={editTarget.supplierName}
      criteriaName={editTarget.criteriaName}
      criteriaId={editTarget.criteriaId}
      inviteId={editTarget.inviteId}
      currentScore={editTarget.currentScore}
      isSaving={upsertScores.isPending}
      onClose={onClose}
      onSave={handleSave}
    />
  );
};

export const SoloAwardView: React.FC<SoloAwardViewProps> = ({
  rfxId,
  evalId,
}) => {
  const { data: overallData, isLoading: scoresLoading, refetch: refetchScores } =
    useOverallScores(rfxId, evalId);
  const { awardsData, isLoading: awardsLoading, upsertAwards } = useAwards(
    rfxId,
    evalId
  );

  const [awardStates, setAwardStates] = useState<Record<number, AwardState>>(
    {}
  );
  const [initialized, setInitialized] = useState(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);

  useEffect(() => {
    if (!initialized && awardsData && overallData) {
      const initial: Record<number, AwardState> = {};
      for (const supplier of overallData.suppliers) {
        const existing = awardsData.awards.find(
          (a) => a.invite_id === supplier.invite_id
        );
        initial[supplier.invite_id] = {
          award_status: existing?.award_status ?? "pending",
          comment: existing?.comment ?? "",
        };
      }
      setAwardStates(initial);
      setInitialized(true);
    }
  }, [awardsData, overallData, initialized]);

  const handleAwardStatusChange = (inviteId: number, status: AwardStatus) => {
    setAwardStates((prev) => {
      const next: Record<number, AwardState> = { ...prev };

      if (status === "awarded") {
        for (const id of Object.keys(next)) {
          const numId = Number(id);
          if (next[numId].award_status === "awarded") {
            next[numId] = { ...next[numId], award_status: "pending" };
          }
        }
      }

      next[inviteId] = { ...next[inviteId], award_status: status };
      return next;
    });
  };

  const handleCommentChange = (inviteId: number, comment: string) => {
    setAwardStates((prev) => ({
      ...prev,
      [inviteId]: { ...prev[inviteId], comment },
    }));
  };

  const handleSaveAwards = () => {
    const decisions = Object.entries(awardStates).map(([inviteId, state]) => ({
      rfx_invite_id: Number(inviteId),
      award_status: state.award_status,
      comment: state.comment || null,
    }));
    upsertAwards.mutate({ decisions });
  };

  const handleEditScore = (
    supplier: OverallSupplierScore,
    criteria: OverallCriteriaScore
  ) => {
    setEditTarget({
      criteriaId: criteria.criteria_id,
      criteriaName: criteria.criteria_name,
      inviteId: supplier.invite_id,
      supplierName: supplier.supplier_name,
      currentScore: criteria.pre_meeting_average,
    });
  };

  const isLoading = scoresLoading || awardsLoading;

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="solo-award-view-loading">
        {[1, 2, 3].map((i) => (
          <SkeletonBase
            key={i}
            startColor="lightgray"
            width="100%"
            height="80px"
            className="!rounded-lg"
          />
        ))}
      </div>
    );
  }

  if (!overallData) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm">
        Score data is not yet available.
      </div>
    );
  }

  const suppliers = overallData.suppliers;
  const leaderId = getLeaderInviteId(suppliers);
  const leaderScore = getLeaderScore(suppliers, leaderId);
  const canFinalize = awardsData?.can_finalize ?? false;
  const isSavingAwards = upsertAwards.isPending;

  // Build criteria list from first supplier (all suppliers share same criteria structure)
  const criteriaList =
    suppliers.length > 0 ? suppliers[0].criteria_scores : [];

  return (
    <div className="space-y-6" data-testid="solo-award-view">
      {/* Score summary table */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">
          Score Summary
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Scores from Step 6 (pre-meeting averages). Use Edit to adjust individual scores with a required reason.
        </p>

        <div className="w-full overflow-x-auto">
          <table
            className="w-full min-w-[600px] border-collapse text-sm"
            data-testid="score-summary-table"
          >
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">Criterion</th>
                <th className="px-3 py-2 text-right w-16">Weight</th>
                {suppliers.map((s) => (
                  <th
                    key={s.invite_id}
                    className="px-3 py-2 text-right min-w-[120px]"
                  >
                    {s.supplier_name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {criteriaList.map((criteria) => (
                <tr
                  key={criteria.criteria_id}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  data-testid={`criteria-row-${criteria.criteria_id}`}
                >
                  <td className="px-3 py-2.5 font-medium text-gray-900">
                    {criteria.criteria_name}
                  </td>
                  <td className="px-3 py-2.5 text-right text-gray-500 tabular-nums">
                    {criteria.criteria_weight}%
                  </td>
                  {suppliers.map((supplier) => {
                    const supplierCriteria = supplier.criteria_scores.find(
                      (c) => c.criteria_id === criteria.criteria_id
                    );
                    return (
                      <td key={supplier.invite_id} className="px-3 py-2.5 text-right">
                        <ScoreCell
                          score={supplierCriteria?.pre_meeting_average ?? null}
                          onEdit={() =>
                            handleEditScore(
                              supplier,
                              supplierCriteria ?? criteria
                            )
                          }
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Award decisions */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">
          Award Decisions
        </h2>

        <div className="space-y-3">
          {suppliers.map((supplier) => (
            <SupplierAwardCard
              key={supplier.invite_id}
              supplier={supplier}
              awardStatus={
                awardStates[supplier.invite_id]?.award_status ?? "pending"
              }
              comment={awardStates[supplier.invite_id]?.comment ?? ""}
              isLeader={supplier.invite_id === leaderId}
              isBelowThreshold={isBelowThreshold(supplier, leaderScore)}
              showAwardControls
              onAwardStatusChange={handleAwardStatusChange}
              onCommentChange={handleCommentChange}
            />
          ))}
        </div>

        <div className="flex justify-end pt-3">
          <button
            type="button"
            onClick={handleSaveAwards}
            disabled={!canFinalize || isSavingAwards}
            className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="save-awards-button"
          >
            {isSavingAwards ? "Saving..." : "Save Award Decisions"}
          </button>
        </div>

        {upsertAwards.isError && (
          <p className="text-sm text-red-600 mt-2" role="alert">
            Failed to save award decisions. Please try again.
          </p>
        )}
      </div>

      {/* Score edit modal — isolated to avoid remounting useMeetingScores */}
      {editTarget && (
        <ScoreEditor
          rfxId={rfxId}
          evalId={evalId}
          editTarget={editTarget}
          onClose={() => setEditTarget(null)}
          onSuccess={refetchScores}
        />
      )}
    </div>
  );
};
